import { Controller } from "@hotwired/stimulus"
import { LEVEL_DEGRADED } from "../field_network_health"
import {
  recordDegradedEntry,
  seedFromBadConnectivity,
  offlineFirstPlaceIds,
  leavePlace,
  dropOtherThresholdLatches
} from "../field_place_connectivity"
import { logEvent } from "../field_logger"

// Latches a place into offline-first page loads once it racks up repeated
// Degraded-connectivity entries in a short window (card #4310 -- "Treat a
// store with repeated degraded connectivity as offline"). See
// field_place_connectivity.js for the full rationale, thresholds and the
// recovery/leave semantics.
//
// Mounted on a `data-turbo-permanent` element inside
// field/shared/_place_bottom_nav.html.erb, which every place sub-page (show,
// details, history, contacts, schedule) renders with an id keyed to the
// place -- so this one controller instance survives Turbo navigation between
// a single place's own pages (not a "leave"), and only disconnects when the
// Tasker actually moves to a different place or away from the place section
// entirely (a real "leave", per the card's "until signal recovers or the
// Tasker leaves" requirement).
//
// field_offline_first_badge_controller.js is the separate, display-only
// counterpart that renders the "Saved data - sync pending" indicator; it is
// NOT turbo-permanent and simply re-reads this module's state fresh on every
// page connect.
//
// Values:
//   placeId         -- the Place#id this nav belongs to
//   badConnectivity -- the place's own bad_connectivity flag (seeds the latch
//                       immediately, card #4310 slice 3)

export default class extends Controller {
  static values = { placeId: Number, badConnectivity: Boolean }

  connect() {
    // Card #4310 follow-up (note 3): a full page navigation away from the
    // PREVIOUS place never ran its disconnect(), so drop any other place's
    // threshold-earned latch before seeding this one -- see
    // field_place_connectivity.js's dropOtherThresholdLatches for the reset
    // semantics (seeded flags are untouched).
    dropOtherThresholdLatches(this.placeIdValue)
    seedFromBadConnectivity(this.placeIdValue, this.badConnectivityValue)
    this._postState()

    this._healthChangedHandler = (e) => this._onHealthChanged(e.detail)
    window.addEventListener("field:health-changed", this._healthChangedHandler)
  }

  disconnect() {
    window.removeEventListener("field:health-changed", this._healthChangedHandler)
    leavePlace(this.placeIdValue)
    this._postState()
  }

  _onHealthChanged({ level }) {
    if (level !== LEVEL_DEGRADED) return

    const justLatched = recordDegradedEntry(this.placeIdValue)
    this._postState()

    if (justLatched) {
      logEvent("connectivity", "Place latched into offline-first page loads", {
        placeId: this.placeIdValue
      }).catch(() => {})
    }
  }

  // Tells the service worker which places are currently offline-first, and
  // lets any mounted badge refresh immediately rather than waiting on the
  // next field:health-changed tick (see field_offline_first_badge_controller.js
  // for why a dedicated event is needed here instead of relying on listener
  // ordering within the same field:health-changed dispatch).
  _postState() {
    const placeIds = offlineFirstPlaceIds()
    navigator.serviceWorker?.controller?.postMessage({ type: "OFFLINE_FIRST_PLACES", placeIds })
    window.dispatchEvent(new CustomEvent("field:offline-first-changed", { detail: { placeIds } }))
  }
}
